import React from "react";
import { Search, MapPin, Star, MousePointer2, Heart, Play, Phone, ShieldCheck, Eye, CheckCircle2, Bot } from "lucide-react";

const G = "#00E676";

const Shell = ({ children, aspect }) => (
  <div className={`dmn-demo relative w-full ${aspect} overflow-hidden bg-[#070908]`} aria-hidden="true">
    <div
      className="absolute inset-0 opacity-[0.05]"
      style={{ backgroundImage: `linear-gradient(${G} 1px, transparent 1px), linear-gradient(90deg, ${G} 1px, transparent 1px)`, backgroundSize: "28px 28px" }}
    />
    <div className="relative h-full flex flex-col justify-center px-5 py-4">{children}</div>
  </div>
);

const Skel = ({ w1 = "w-2/3", w2 = "w-1/2" }) => (
  <div className="rounded-lg bg-white/[0.05] border border-white/[0.08] px-3 py-1.5 h-7 flex flex-col justify-center gap-1">
    <div className={`h-1 rounded bg-white/20 ${w1}`} />
    <div className={`h-1 rounded bg-white/10 ${w2}`} />
  </div>
);

const GreenChip = ({ children, delay = "0s", className = "" }) => (
  <span
    className={`dmn-a dmn-pop inline-flex items-center gap-1 text-[9px] font-extrabold bg-[#00E676] text-black rounded px-1.5 py-0.5 ${className}`}
    style={{ animationDelay: delay }}
  >
    {children}
  </span>
);

const Stars = ({ delayBase = 0.6, size = "w-2.5 h-2.5" }) => (
  <span className="flex gap-0.5">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`dmn-a dmn-pop ${size}`} fill={G} stroke="none" style={{ animationDelay: `${delayBase + i * 0.15}s` }} />
    ))}
  </span>
);

/* SEO — your result climbs to #1 on a SERP */
const SeoDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-3 h-7 mb-3">
      <Search className="w-3 h-3" style={{ color: G }} />
      <span className="text-[10px] text-gray-300">best seo company dallas</span>
    </div>
    <div className="relative h-[88px]">
      <div className="dmn-rank-down absolute top-0 inset-x-0"><Skel /></div>
      <div className="dmn-rank-down absolute top-8 inset-x-0" style={{ animationDelay: "0.05s" }}><Skel w1="w-1/2" w2="w-2/3" /></div>
      <div className="dmn-rank-up absolute top-16 inset-x-0">
        <div className="rounded-lg border border-[#00E676]/60 bg-[#00E676]/10 px-3 h-7 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: G }} />
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-white leading-tight truncate">Digital Media Ninja</p>
            <p className="text-[8px] text-gray-400 leading-tight truncate">digitalmedianinja.com</p>
          </div>
          <GreenChip delay="3.2s" className="ml-auto">#1</GreenChip>
        </div>
      </div>
    </div>
  </Shell>
);

/* Local SEO — "near me" search, pin drops, GBP listing found */
const LocalSeoDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-3 h-7 mb-3">
      <Search className="w-3 h-3" style={{ color: G }} />
      <span className="text-[10px] text-gray-300">plumber near me</span>
      <span className="w-px h-3 bg-[#00E676] animate-pulse" />
    </div>
    <div className="relative rounded-lg border border-white/10 bg-white/[0.03] h-16 mb-2 overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `linear-gradient(${G} 1px, transparent 1px), linear-gradient(90deg, ${G} 1px, transparent 1px)`, backgroundSize: "14px 14px" }} />
      <MapPin className="dmn-pin absolute left-1/2 top-1/2 -ml-2.5 -mt-4 w-5 h-5" style={{ color: G }} fill={G} fillOpacity={0.3} />
    </div>
    <div className="dmn-a dmn-rise rounded-lg border border-[#00E676]/50 bg-[#0C1310] px-3 py-1.5 flex items-center gap-2" style={{ animationDelay: "1.6s" }}>
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-white leading-tight truncate">Your Business</p>
        <span className="flex items-center gap-1"><Stars delayBase={2.2} size="w-2 h-2" /><span className="text-[8px] text-gray-400">4.9 · 0.4 mi</span></span>
      </div>
      <GreenChip delay="3.4s" className="ml-auto">Found</GreenChip>
    </div>
  </Shell>
);

/* PPC — cursor clicks the top sponsored ad */
const PpcDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="relative">
      <div className="dmn-flash relative rounded-lg border px-3 py-1.5 mb-2" style={{ borderColor: "rgba(0,230,118,.35)", background: "rgba(0,230,118,.08)" }}>
        <span className="text-[8px] font-extrabold uppercase tracking-wider" style={{ color: G }}>Sponsored</span>
        <p className="text-[10px] font-bold text-white leading-tight">Your Ad — Top of Google</p>
        <div className="h-1 rounded bg-white/15 w-3/4 mt-1" />
        <span className="dmn-ripple absolute right-6 top-1/2 -mt-3 w-6 h-6 rounded-full border-2" style={{ borderColor: G }} />
        <MousePointer2 className="dmn-cursor absolute right-7 top-1/2 -mt-1 w-4 h-4 text-white" fill="white" />
      </div>
      <div className="space-y-2">
        <Skel />
        <Skel w1="w-1/2" w2="w-2/3" />
      </div>
      <GreenChip delay="3.6s" className="absolute -top-1 right-0">+1 Lead ✓</GreenChip>
    </div>
  </Shell>
);

/* Web Design — page assembles, engagement climbs */
const WebDesignDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-white/10 bg-white/[0.04]">
        <span className="w-1.5 h-1.5 rounded-full bg-white/25" /><span className="w-1.5 h-1.5 rounded-full bg-white/25" /><span className="w-1.5 h-1.5 rounded-full bg-white/25" />
      </div>
      <div className="p-2.5">
        <div className="dmn-a dmn-pop h-2 rounded bg-white/20 w-1/2 mb-1.5" style={{ animationDelay: "0.3s" }} />
        <div className="dmn-a dmn-pop h-6 rounded bg-[#00E676]/15 border border-[#00E676]/30 mb-1.5" style={{ animationDelay: "0.7s" }} />
        <div className="flex gap-1.5 mb-1.5">
          <span className="dmn-a dmn-pop h-3 w-12 rounded-full" style={{ background: G, animationDelay: "1.1s" }} />
          <span className="dmn-a dmn-pop h-3 w-12 rounded-full border border-white/25" style={{ animationDelay: "1.3s" }} />
        </div>
        <div className="flex items-end justify-between">
          <svg viewBox="0 0 90 28" className="w-24 h-7">
            <polyline className="dmn-draw" points="2,26 20,22 38,23 56,14 74,10 88,3" fill="none" stroke={G} strokeWidth="2" strokeLinecap="round" />
          </svg>
          <GreenChip delay="3.8s">Engagement +64%</GreenChip>
        </div>
      </div>
    </div>
  </Shell>
);

/* Content Marketing — article writes itself, readers roll in */
const ContentDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="h-2 rounded w-2/3 mb-2.5" style={{ background: G, opacity: 0.7 }} />
      {["w-full", "w-11/12", "w-4/5", "w-2/3"].map((w, i) => (
        <div key={i} className={`dmn-linegrow h-1.5 rounded bg-white/15 mb-1.5 ${w}`} style={{ animationDelay: `${0.3 + i * 0.35}s` }} />
      ))}
      <div className="flex items-center gap-2 mt-2.5">
        <GreenChip delay="3.2s"><Eye className="w-2.5 h-2.5" /> 2.4K reads</GreenChip>
        <GreenChip delay="3.7s">Cited by AI ✓</GreenChip>
      </div>
    </div>
  </Shell>
);

/* Social Media — likes float, followers grow */
const SocialDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="relative rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-5 h-5 rounded-full bg-[#00E676]/30 border border-[#00E676]/50" />
        <div><div className="h-1.5 w-16 rounded bg-white/25 mb-1" /><div className="h-1 w-10 rounded bg-white/10" /></div>
      </div>
      <div className="h-10 rounded bg-gradient-to-br from-[#00E676]/25 to-white/[0.04] border border-white/10 mb-2" />
      <div className="flex items-center gap-2">
        <Heart className="w-3 h-3" fill={G} stroke="none" />
        <GreenChip delay="2.6s">+128 likes</GreenChip>
        <GreenChip delay="3.4s">+42 followers</GreenChip>
      </div>
      {[0, 1, 2].map((i) => (
        <Heart key={i} className="dmn-heart absolute w-3.5 h-3.5" fill={G} stroke="none"
          style={{ right: `${14 + i * 16}px`, bottom: "22px", animationDelay: `${i * 1.1}s` }} />
      ))}
    </div>
  </Shell>
);

/* GBP — profile completes, stars fill, actions pulse */
const GbpDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-bold text-white">Your Business</p>
        <GreenChip delay="3.2s">Verified ✓</GreenChip>
      </div>
      <span className="flex items-center gap-1.5 mb-2"><Stars delayBase={0.5} /><span className="text-[8px] text-gray-400">4.9 (212)</span></span>
      <div className="flex gap-1 mb-2">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="dmn-a dmn-pop h-5 flex-1 rounded bg-[#00E676]/15 border border-[#00E676]/25" style={{ animationDelay: `${1.4 + i * 0.2}s` }} />
        ))}
      </div>
      <div className="flex gap-1.5">
        <span className="dmn-pulse text-[8px] font-bold text-black rounded-full px-2 py-0.5" style={{ background: G }}>Call</span>
        <span className="text-[8px] font-bold rounded-full px-2 py-0.5 border" style={{ color: G, borderColor: G }}>Directions</span>
      </div>
    </div>
  </Shell>
);

/* Reputation — reviews arrive, rating climbs */
const ReputationDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="flex items-center gap-3 mb-2.5">
      <span className="text-2xl font-extrabold text-white leading-none">4.9</span>
      <Stars delayBase={0.4} />
      <ShieldCheck className="dmn-a dmn-pop w-4 h-4 ml-auto" style={{ color: G, animationDelay: "3s" }} />
    </div>
    {[0, 1].map((i) => (
      <div key={i} className="dmn-a dmn-rise rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 mb-1.5 flex items-center gap-2" style={{ animationDelay: `${1 + i * 0.7}s` }}>
        <span className="w-4 h-4 rounded-full bg-[#00E676]/25 border border-[#00E676]/40 shrink-0" />
        <div className="min-w-0 flex-1"><div className="h-1 rounded bg-white/20 w-3/4 mb-1" /><div className="h-1 rounded bg-white/10 w-1/2" /></div>
        <span className="flex gap-0.5">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-2 h-2" fill={G} stroke="none" />)}</span>
      </div>
    ))}
    <GreenChip delay="3.6s" className="self-start">New 5-star review ✓</GreenChip>
  </Shell>
);

/* Email & SMS — messages land, opens climb */
const EmailSmsDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="space-y-1.5 mb-2">
      {[
        { d: "0.4s", side: "", w: "w-4/5" },
        { d: "1.2s", side: "ml-auto", w: "w-3/5" },
        { d: "2s", side: "", w: "w-2/3" },
      ].map((b, i) => (
        <div key={i} className={`dmn-a dmn-rise ${b.side} ${b.w} rounded-xl border px-2.5 py-1.5 ${i === 1 ? "bg-[#00E676]/15 border-[#00E676]/40 rounded-br-sm" : "bg-white/[0.05] border-white/10 rounded-bl-sm"}`} style={{ animationDelay: b.d }}>
          <div className="h-1 rounded bg-white/25 w-full mb-1" /><div className="h-1 rounded bg-white/10 w-2/3" />
        </div>
      ))}
    </div>
    <div className="flex gap-2">
      <GreenChip delay="3s">68% open rate</GreenChip>
      <GreenChip delay="3.5s">Reply in 12s ⚡</GreenChip>
    </div>
  </Shell>
);

/* AEO/GEO — AI assistant cites your brand */
const AeoGeoDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="ml-auto w-4/5 rounded-xl rounded-br-sm bg-white/[0.06] border border-white/10 px-2.5 py-1.5 mb-2">
      <p className="text-[9px] text-gray-300">best marketing agency in dallas?</p>
    </div>
    <div className="flex items-start gap-1.5 w-11/12">
      <Bot className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: G }} />
      <div className="dmn-a dmn-rise flex-1 rounded-xl rounded-bl-sm bg-[#0C1310] border border-[#00E676]/30 px-2.5 py-2" style={{ animationDelay: "1.2s" }}>
        <span className="flex gap-1 mb-1.5">
          {[0, 1, 2].map((i) => <span key={i} className="dmn-typedot w-1 h-1 rounded-full" style={{ background: G, animationDelay: `${i * 0.18}s` }} />)}
        </span>
        <div className="dmn-linegrow h-1 rounded bg-white/20 w-full mb-1" style={{ animationDelay: "2s" }} />
        <div className="dmn-linegrow h-1 rounded bg-white/15 w-4/5 mb-1.5" style={{ animationDelay: "2.3s" }} />
        <GreenChip delay="3.4s">digitalmedianinja.com ✓</GreenChip>
      </div>
    </div>
  </Shell>
);

/* Video Marketing — player runs, views climb */
const VideoDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="relative rounded-lg border border-white/10 bg-gradient-to-br from-[#00E676]/10 to-white/[0.03] overflow-hidden">
      <div className="h-20 flex items-center justify-center">
        <span className="relative flex items-center justify-center">
          <span className="dmn-ring absolute w-9 h-9 rounded-full border-2" style={{ borderColor: G }} />
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: G }}>
            <Play className="w-4 h-4 text-black ml-0.5" fill="black" />
          </span>
        </span>
        <GreenChip delay="2.8s" className="absolute top-2 right-2"><Eye className="w-2.5 h-2.5" /> 12.5K</GreenChip>
      </div>
      <div className="h-1 bg-white/10"><div className="dmn-progress h-full w-full" style={{ background: G }} /></div>
    </div>
  </Shell>
);

/* AI Receptionist — call answered, appointment booked */
const AiReceptionistDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="flex items-center gap-2.5 mb-2.5">
      <span className="relative flex items-center justify-center shrink-0">
        <span className="dmn-ring absolute w-8 h-8 rounded-full border-2" style={{ borderColor: G }} />
        <span className="w-8 h-8 rounded-full bg-[#00E676]/20 border border-[#00E676]/50 flex items-center justify-center">
          <Phone className="w-3.5 h-3.5" style={{ color: G }} />
        </span>
      </span>
      <div>
        <p className="text-[10px] font-bold text-white leading-tight">Incoming call…</p>
        <p className="text-[8px]" style={{ color: G }}>AI answered · 0 rings missed</p>
      </div>
      <span className="ml-auto flex items-end gap-0.5 h-5">
        {[0.6, 1, 0.4, 0.9, 0.5].map((h, i) => (
          <span key={i} className="dmn-wave w-1 rounded-full" style={{ height: `${h * 20}px`, background: G, animationDelay: `${i * 0.12}s` }} />
        ))}
      </span>
    </div>
    <div className="dmn-a dmn-rise rounded-lg border border-[#00E676]/40 bg-[#0C1310] px-3 py-2 flex items-center gap-2" style={{ animationDelay: "2.2s" }}>
      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: G }} />
      <p className="text-[9px] text-white font-semibold">Appointment booked — Tue 10:30 AM</p>
    </div>
  </Shell>
);

/* Default — growth chart */
const DefaultDemo = ({ aspect }) => (
  <Shell aspect={aspect}>
    <div className="flex items-end gap-2 h-20 mb-2">
      {[0.35, 0.5, 0.42, 0.65, 0.8, 1].map((h, i) => (
        <span key={i} className="dmn-a dmn-pop flex-1 rounded-t" style={{ height: `${h * 100}%`, background: i === 5 ? G : "rgba(0,230,118,.25)", animationDelay: `${0.3 + i * 0.25}s` }} />
      ))}
    </div>
    <GreenChip delay="2.6s" className="self-start">Growth, measured ✓</GreenChip>
  </Shell>
);

const DEMOS = {
  seo: SeoDemo,
  "local-seo": LocalSeoDemo,
  ppc: PpcDemo,
  "web-design": WebDesignDemo,
  "content-marketing": ContentDemo,
  "social-media": SocialDemo,
  "gbp-optimization": GbpDemo,
  "reputation-management": ReputationDemo,
  "email-sms-marketing": EmailSmsDemo,
  "aeo-geo": AeoGeoDemo,
  "video-marketing": VideoDemo,
  "ai-receptionist": AiReceptionistDemo,
};

export const ServiceDemo = ({ slug, aspect = "aspect-[16/10]" }) => {
  const Demo = DEMOS[slug] || DefaultDemo;
  return <Demo aspect={aspect} />;
};

export default ServiceDemo;
